import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { FillScreen } from "@/components/Layout";
import { defaultNS } from "@/lib/i18n";
import { LIST_EMPTY_DATA } from "@/lib/listPage";

import { AppsAlertHelpText as HelpText } from "../../../../-components/apps-alerts-help-text";
import {
  AppsAlertTabs,
  useAppsAlertTabs,
} from "../../../../-components/apps-alerts-tabs";
import { AppsAlertType } from "../../../../-zod-schema";

import { ShadowItAppsFilter as Filter } from "./-components/filter";
import { ShadowItAppListTable as Table } from "./-components/table";
import { useExportShadowItApps, useShadowItApps } from "./-query";
import { type SearchParams, SearchSchema } from "./-zod-schema";

export const Route = createFileRoute(
  "/_staff/apps/_list/alerts/_alerts/shadow-it/_shadow-it/apps/",
)({
  validateSearch: zodValidator(SearchSchema),
  component: ShadowItApps,
});

function ShadowItApps() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.alert",
  });

  // 共通系（シャドーITのログを表示するためにコンポーネントを切り分けている）
  const counts = useLoaderData({
    from: Route.parentRoute.parentRoute.parentRoute.id,
  });
  const { onChangeTab, emptyCounts } = useAppsAlertTabs();

  // ここからシャドーITの話
  const { data, isPending } = useShadowItApps(search);

  const { mutate: onClickExport, isPending: exporting } =
    useExportShadowItApps(search);

  const onSearch = useCallback(
    (query: Partial<SearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) });
    },
    [navigate],
  );

  const onChangeFilter = useCallback(
    // 検索条件を変更したら1ページ目から検索する
    (query: Partial<SearchParams>) => onSearch({ ...query, pageIndex: 0 }),
    [onSearch],
  );

  return (
    <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <AppsAlertTabs
        value={AppsAlertType.noWorker.key}
        onChange={onChangeTab}
        counts={counts ?? emptyCounts}
      />
      <HelpText
        title={t("help.shadowIt.title")}
        text={t("help.shadowIt.text")}
      />
      <Filter
        state={search}
        onChange={onChangeFilter}
        exporting={exporting}
        onClickExport={onClickExport}
      />
      <Table
        data={data ?? LIST_EMPTY_DATA}
        search={search}
        onSearch={onSearch}
        isPending={isPending}
      />
    </FillScreen>
  );
}
